import React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Box, Grid, Paper, TextField, Typography } from '@mui/material';

import { LabeledField } from '@/__core/components/LabeledField';
import UploadImage from '@/__core/helpers/components/UploadImage';
import { AddButtonAutocomplete } from '@/__core/hocs';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { ItemAddOptionModal, ItemSupplierAddModal } from './modals';
import {
  createItemManufacturerOption,
  createItemSupplierOption,
  getItemTypeOptions,
  InventoryItemFormData,
  InventoryItemOption,
} from '../models/ItemModel';

interface ItemInformationTabProps {
  formData: InventoryItemFormData;
  supplierOptions: InventoryItemOption[];
  manufacturerOptions: InventoryItemOption[];
  groupOptions: InventoryItemOption[];
  uomOptions: InventoryItemOption[];
  onChangeForm: (updates: Partial<InventoryItemFormData>) => void;
  canEdit: boolean;
  canCreateSupplier: boolean;
  active: boolean;
}

type AddOptionCustomDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<any>;
  loading?: boolean;
};

const ManufacturerAddModal: React.FC<AddOptionCustomDialogProps> = (props) => (
  <ItemAddOptionModal {...props} title='Thêm nhà sản xuất' label='Tên nhà sản xuất' placeholder='Nhập tên nhà sản xuất' />
);

export default function ItemInformationTab({
  formData,
  supplierOptions,
  manufacturerOptions,
  groupOptions,
  uomOptions,
  onChangeForm,
  canEdit,
  canCreateSupplier,
  active,
}: ItemInformationTabProps) {
  if (!active) return null;
  const readOnly = !canEdit;
  const selectedGroup = groupOptions.find((option) => option.id === formData.group_id) || null;
  const selectedSupplier = supplierOptions.find((option) => option.id === formData.supplier_id) || null;
  const itemTypeOptions = getItemTypeOptions(formData.company_id);
  const selectedItemType = itemTypeOptions.find((option) => option.id === formData.item_type) || null;
  const selectedManufacturer = manufacturerOptions.find((option) => option.id === formData.manufacturer_id) || null;
  const selectedUom = uomOptions.find((option) => option.id === formData.uom_id) || null;
  const SupplierAddModal: React.FC<AddOptionCustomDialogProps> = (props) => <ItemSupplierAddModal {...props} />;

  const convertFileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Không thể đọc tệp hình ảnh'));
    reader.readAsDataURL(file);
  });

  const handleImageAdded = (field: 'image1' | 'image2') => async (imageFile: any) => {
    if (readOnly || !imageFile?.file) return;
    onChangeForm({ [field]: await convertFileToDataUrl(imageFile.file as File) });
  };

  const handleImageRemoved = (field: 'image1' | 'image2') => () => {
    if (!readOnly) onChangeForm({ [field]: '' });
  };

  return (
    <>
      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', boxSizing: 'border-box' }}>
        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Mã vật tư' labelWidth={100}>
              <TextField size='small' value={formData.code} onChange={(event) => onChangeForm({ code: event.target.value })} fullWidth disabled={readOnly} autoComplete='off' />
            </LabeledField>
            <LabeledField label='Tên vật tư' labelWidth={100}>
              <TextField size='small' value={formData.name} onChange={(event) => onChangeForm({ name: event.target.value })} fullWidth disabled={readOnly} autoComplete='off' />
            </LabeledField>
            <LabeledField label='Nhóm vật tư' labelWidth={100}>
              <AddButtonAutocomplete options={groupOptions} value={selectedGroup} onChange={(_event, value) => onChangeForm({ group_id: (value as InventoryItemOption | null)?.id || '' })} placeholder='Chọn nhóm vật tư' noOptionsText='Không tìm thấy nhóm vật tư' disabled={readOnly} fullWidth />
            </LabeledField>
            <LabeledField label='Nhà cung cấp' labelWidth={100}>
              <AddButtonAutocomplete
                options={supplierOptions}
                value={selectedSupplier}
                onChange={(_event, value) => onChangeForm({ supplier_id: (value as InventoryItemOption | null)?.id || '' })}
                placeholder='Chọn nhà cung cấp'
                noOptionsText='Không tìm thấy nhà cung cấp'
                CustomDialog={SupplierAddModal}
                onAddNewCustom={async (data) => {
                  const created = await createItemSupplierOption(data.name, formData.company_id, { email: data.email, phone: data.phone, address: data.address });
                  onChangeForm({ supplier_id: created.id });
                  return created;
                }}
                disabled={readOnly}
                canAddNew={!readOnly && canCreateSupplier}
                fullWidth
              />
            </LabeledField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label='Loại vật tư' labelWidth={100}>
              <AddButtonAutocomplete options={itemTypeOptions} value={selectedItemType} onChange={(_event, value) => onChangeForm({ item_type: (value as InventoryItemOption | null)?.id || '' })} placeholder='Chọn loại vật tư' noOptionsText='Không tìm thấy loại vật tư' disabled={readOnly} fullWidth />
            </LabeledField>
            <LabeledField label='Nhà sản xuất' labelWidth={100}>
              <AddButtonAutocomplete
                options={manufacturerOptions}
                value={selectedManufacturer}
                onChange={(_event, value) => onChangeForm({ manufacturer_id: (value as InventoryItemOption | null)?.id || '' })}
                placeholder='Chọn nhà sản xuất'
                noOptionsText='Không tìm thấy nhà sản xuất'
                CustomDialog={ManufacturerAddModal}
                onAddNewCustom={async (data) => {
                  const created = await createItemManufacturerOption(data.name, formData.company_id);
                  onChangeForm({ manufacturer_id: created.id });
                  return created;
                }}
                disabled={readOnly}
                canAddNew={!readOnly}
                fullWidth
              />
            </LabeledField>
            <LabeledField label='ĐVT' labelWidth={100}>
              <AddButtonAutocomplete options={uomOptions} value={selectedUom} onChange={(_event, value) => onChangeForm({ uom_id: (value as InventoryItemOption | null)?.id || '' })} placeholder='Chọn ĐVT' noOptionsText='Không tìm thấy ĐVT' disabled={readOnly} fullWidth />
            </LabeledField>
          </Grid>
        </Grid>
        <Grid container rowSpacing={1} sx={sharedFormStyles.additionalTextGrid} mt={1}>
          <Grid size={{ xs: 12 }}>
            <LabeledField label='Ghi chú' labelWidth={100} alignItems='start'>
              <TextareaAutosize minRows={2} value={formData.specification} onChange={(event) => onChangeForm({ specification: event.target.value })} placeholder='Nhập ghi chú kỹ thuật hoặc mô tả vật tư...' disabled={readOnly} style={sharedFormStyles.additionalTextarea(canEdit)} />
            </LabeledField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ ...sharedFormStyles.formCard, mb: 2, width: '100%', boxSizing: 'border-box' }}>
        <Typography sx={sharedFormStyles.sectionTitle}>Hình ảnh vật tư</Typography>
        <Box sx={sharedFormStyles.additionalImagesContainer}>
          {(['image1', 'image2'] as const).map((field, index) => (
            <Box key={field} sx={sharedFormStyles.additionalImageItem}>
              <Box sx={sharedFormStyles.additionalImageCard}>
                <Typography sx={sharedFormStyles.additionalImageTitle}>Hình ảnh {index + 1}</Typography>
                <Box sx={sharedFormStyles.additionalImageUploadWrapper}>
                  <UploadImage currentImageUrl={formData[field] || undefined} onFileAdded={handleImageAdded(field)} onFileRemoved={handleImageRemoved(field)} disabled={readOnly} height={sharedFormStyles.additionalImageUploadHeight} background={sharedFormStyles.additionalImageUploadBackground} borderRadius={sharedFormStyles.additionalImageUploadBorderRadius} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </>
  );
}
